import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Chip,
  Divider,
  IconButton,
  Link,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Groups as GroupsIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { getMatchConfidenceLevel } from '../../utils/committeeImportTemplate';
import { committeeTypeLabel } from '../../constants/committeeTypes';

const CommitteeDuplicateResolutionPanel = ({ open, committee, onClose, onResolve }) => {
  const [selectedAction, setSelectedAction] = useState('');

  const handleResolve = () => {
    if (!selectedAction) return;

    onResolve({
      committeeIndex: committee._rowIndex,
      action: selectedAction,
      existingCommitteeId: selectedAction.startsWith('map-')
        ? selectedAction.replace('map-', '')
        : null,
    });
  };

  const renderCommitteeInfo = (data, title, isImported = false) => (
    <Paper sx={{ p: 2, mb: 2, backgroundColor: isImported ? '#f0f7ff' : '#f5f5f5' }}>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Box display="flex" flexDirection="column" gap={1}>
        {data.name && (
          <Box display="flex" alignItems="center" gap={1}>
            <GroupsIcon fontSize="small" color="action" />
            <Typography variant="body2">{data.name}</Typography>
          </Box>
        )}
        {(data.companyName || data.company) && (
          <Box display="flex" alignItems="center" gap={1}>
            <BusinessIcon fontSize="small" color="action" />
            <Typography variant="body2">{data.companyName || data.company}</Typography>
          </Box>
        )}
        {(data.type || data.typeCode) && (
          <Typography variant="body2" color="textSecondary">
            Type: {committeeTypeLabel(data.type || data.typeCode)}
          </Typography>
        )}
        {data.chairperson && (
          <Typography variant="body2" color="textSecondary">
            Chairperson: {data.chairperson}
          </Typography>
        )}
      </Box>
    </Paper>
  );

  const renderMatchOption = (match, index) => {
    const confidence = getMatchConfidenceLevel(match.score);

    return (
      <Box key={match.id} sx={{ mb: 2 }}>
        <FormControlLabel
          value={`map-${match.id}`}
          control={<Radio />}
          label={(
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <Typography variant="body2" fontWeight={500}>
                  Match #{index + 1}
                </Typography>
                <Chip
                  label={confidence.label}
                  size="small"
                  color={confidence.color}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
                <Chip
                  label={`${match.score}% match`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              </Box>
            </Box>
          )}
          sx={{ alignItems: 'flex-start', mb: 1 }}
        />
        <Box sx={{ ml: 4 }}>
          {renderCommitteeInfo(match, '', false)}
          {match.id && (
            <Link
              href={`/committees/${match.id}`}
              target="_blank"
              variant="caption"
              sx={{ display: 'inline-block', mb: 1 }}
            >
              View Committee →
            </Link>
          )}
        </Box>
      </Box>
    );
  };

  if (!committee) return null;

  const hasMatches = committee.matches && committee.matches.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { height: '90vh', maxHeight: '900px' } }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Resolve {committee.status === 'duplicate' ? 'Duplicate' : 'Error'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {renderCommitteeInfo(committee, `Imported Committee (Row #${committee._rowIndex + 1})`, true)}

        {committee.errors?.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Validation Errors:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {committee.errors.map((error, idx) => (
                <li key={idx}>
                  <Typography variant="body2">{error}</Typography>
                </li>
              ))}
            </ul>
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Resolution Options
        </Typography>

        <RadioGroup value={selectedAction} onChange={(e) => setSelectedAction(e.target.value)}>
          {hasMatches && (
            <>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Found {committee.matches.length} potential {committee.matches.length === 1 ? 'match' : 'matches'}:
              </Typography>
              {committee.matches.map((match, index) => renderMatchOption(match, index))}
              <Divider sx={{ my: 2 }} />
            </>
          )}

          <FormControlLabel
            value="create-new"
            control={<Radio />}
            label={(
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Create as New Committee
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Add this as a new committee in the system
                </Typography>
              </Box>
            )}
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            value="skip"
            control={<Radio />}
            label={(
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Skip This Entry
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Do not import this committee
                </Typography>
              </Box>
            )}
          />
        </RadioGroup>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleResolve} disabled={!selectedAction}>
          Apply Resolution
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CommitteeDuplicateResolutionPanel;
